// features/notifications/notificationsSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  query,
  orderBy,
  where
} from "firebase/firestore";
import { db } from "../../app/firebase";

// Backend-only fields (Firestore Timestamp) — not kept in redux
const toNotificationState = ({ sendAt, pending, ...rest }) => rest;

// Fetch all notifications for user
export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async (userId) => {
    const notificationsRef = collection(db, "users", userId, "notifications");
    const q = query(notificationsRef, orderBy("notificationDate", "asc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => toNotificationState({ id: doc.id, ...doc.data() }));
  }
);

// Add a notification
export const addNotification = createAsyncThunk(
  "notifications/addNotification",
  async ({ userId, notificationData }) => {
    const notificationsRef = collection(db, "users", userId, "notifications");
    const docRef = await addDoc(notificationsRef, notificationData);
    return toNotificationState({ id: docRef.id, ...notificationData });
  }
);

// Mark as read
export const markNotificationAsRead = createAsyncThunk(
  "notifications/markAsRead",
  async ({ userId, notificationId }) => {
    const notificationRef = doc(db, "users", userId, "notifications", notificationId);
    await updateDoc(notificationRef, { read: true });
    return notificationId;
  }
);

// Delete
export const deleteNotification = createAsyncThunk(
  "notifications/deleteNotification",
  async ({ userId, notificationId }) => {
    const notificationRef = doc(db, "users", userId, "notifications", notificationId);
    await deleteDoc(notificationRef);
    return notificationId;
  }
);

// Delete all notifications linked to a specific event
export const deleteNotificationsByEventId = createAsyncThunk(
  "notifications/deleteByEventId",
  async ({ userId, eventId }) => {
    const notificationsRef = collection(db, "users", userId, "notifications");
    const q = query(notificationsRef, where("eventId", "==", eventId));
    const snapshot = await getDocs(q);

    const batchDeletes = snapshot.docs.map((docSnap) =>
      deleteDoc(docSnap.ref)
    );

    await Promise.all(batchDeletes);
    return eventId;
  }
);


const notificationsSlice = createSlice({
  name: "notifications",
  initialState: {
    list: [],
    status: "idle",
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.list = action.payload;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })

      .addCase(addNotification.fulfilled, (state, action) => {
        state.list.push(action.payload);
      })

      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const item = state.list.find((n) => n.id === action.payload);
        if (item) item.read = true;
      })

      .addCase(deleteNotification.fulfilled, (state, action) => {
        state.list = state.list.filter((n) => n.id !== action.payload);
      });
  },
});

export default notificationsSlice.reducer;
